/**
 * Treeline silhouette SVG for footer decoration
 * Story 8.7: Creates grounding element at bottom of page
 *
 * Design notes (from mood-story-sagamoon.md):
 * - Nordic forest silhouette
 * - Subtle, doesn't dominate
 * - Desktop only (hidden on mobile to preserve space)
 * - Should feel like the bottom of a storybook page
 *
 * @module components/decorative/TreelineSilhouette
 */

interface TreelineSilhouetteProps {
  /** Additional CSS classes */
  className?: string;
  /** Color of the silhouette (default: twilight deep purple) */
  color?: string;
}

/**
 * Nordic treeline silhouette
 * Displays a gentle forest silhouette at the bottom of the viewport
 * Hidden on mobile (md:block)
 */
export function TreelineSilhouette({
  className = '',
  color = '#2a1f3d',
}: TreelineSilhouetteProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 1440 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`w-full h-auto hidden md:block ${className}`}
      preserveAspectRatio="xMidYMax slice"
      aria-hidden="true"
    >
      {/* Layer 1: Distant trees (lighter, more purple) */}
      <path
        d="M0 120V90
           L30 85 L45 60 L60 85
           L90 80 L105 50 L120 80
           L150 85 L180 75 L195 45 L210 75
           L240 80 L270 70 L285 40 L300 70
           L330 75 L360 80 L390 65 L405 35 L420 65
           L450 75 L480 70 L510 60 L525 30 L540 60
           L570 70 L600 75 L630 65 L645 38 L660 65
           L690 70 L720 75 L750 68 L765 42 L780 68
           L810 72 L840 78 L870 70 L885 45 L900 70
           L930 75 L960 72 L990 65 L1005 40 L1020 65
           L1050 70 L1080 75 L1110 68 L1125 48 L1140 68
           L1170 72 L1200 78 L1230 70 L1245 52 L1260 70
           L1290 75 L1320 80 L1350 72 L1365 55 L1380 72
           L1410 78 L1440 85
           V120 Z"
        fill={color}
        fillOpacity="0.3"
      />

      {/* Layer 2: Mid-distance trees (medium opacity) */}
      <path
        d="M0 120V100
           L40 95 L55 70 L70 95
           L100 92 L130 88 L145 55 L160 88
           L190 90 L220 85 L250 80 L265 48 L280 80
           L310 85 L340 82 L370 78 L385 52 L400 78
           L430 82 L460 85 L490 80 L505 55 L520 80
           L550 83 L580 88 L610 82 L625 58 L640 82
           L670 85 L700 90 L730 84 L745 60 L760 84
           L790 88 L820 92 L850 86 L865 62 L880 86
           L910 90 L940 88 L970 82 L985 58 L1000 82
           L1030 86 L1060 90 L1090 84 L1105 60 L1120 84
           L1150 88 L1180 92 L1210 86 L1225 65 L1240 86
           L1270 90 L1300 94 L1330 88 L1345 68 L1360 88
           L1390 92 L1420 96 L1440 98
           V120 Z"
        fill={color}
        fillOpacity="0.5"
      />

      {/* Layer 3: Foreground trees (darkest, most detail) */}
      <path
        d="M0 120V105
           L20 102 L35 75 L50 102
           L80 100 L95 72 L110 100
           L140 98 L170 95 L185 65 L200 95
           L230 97 L260 94 L290 90 L305 60 L320 90
           L350 93 L380 96 L410 92 L425 68 L440 92
           L470 94 L500 97 L530 93 L545 70 L560 93
           L590 95 L620 98 L650 94 L665 72 L680 94
           L710 96 L740 99 L770 95 L785 74 L800 95
           L830 97 L860 100 L890 96 L905 76 L920 96
           L950 98 L980 96 L1010 92 L1025 70 L1040 92
           L1070 95 L1100 98 L1130 94 L1145 72 L1160 94
           L1190 96 L1220 99 L1250 95 L1265 75 L1280 95
           L1310 97 L1340 100 L1370 96 L1385 78 L1400 96
           L1420 98 L1440 100
           V120 Z"
        fill={color}
        fillOpacity="0.7"
      />
    </svg>
  );
}
